"use client";
import { IBlocks } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import BlockToggle from "./tabs/toggle";
import TwitterFeedOptions from "./tabs/twitterFeed/options";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";
interface Props {
    admin?: boolean;
    block: IBlocks;
    update: BlockUpdateFunction;
    newBlock?: string | null;
}
export default function BlockTwitterFeed({
    admin,
    block,
    update,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Twitter/X Feed",
            description: "",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <TwitterFeedOptions
                        key={block.id}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <Alert className="border-error text-copy w-full bg-destructive/30">
                        <AlertTitle>Important</AlertTitle>
                        <AlertDescription className="max-w-prose">
                            We've noticed that some accounts are not working and
                            it states 'nothing to see here'. This is outside of
                            our control, as we're at the whim of the Twitter
                            gods. Hope you understand.
                        </AlertDescription>
                    </Alert>
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Twitter Feed"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`relative flex w-full flex-row flex-wrap gap-2 p-2`}
                id="twitterTimeline"
            >
                <iframe
                    src={`https://syndication.twitter.com/srv/timeline-profile/screen-name/${blockUpdate.url}?theme=dark&hideScrollBar=true&hideBorder=true`}
                    width={"100%"}
                    height={"500px"}
                    className="rounded-lg"
                />
            </div>
        </AdminPopover>
    );
}
